// Static, realistic-looking content for Dream Technology / DreamAxis marketing site.

export const COMPANY = {
  legalName: "Dream Technology sp. z o.o.",
  tradeName: "DreamAxis",
  domain: "dreamaxis.net",
  email: "business@dreamaxis.net",
  regon: "7011316271",
  founded: 2019,
  hqCity: "Warsaw",
  hqCountry: "Poland",
  hqAddress: "ul. Hoża 86, 00-682 Warsaw, Poland",
  tagline: "Architecting global impact in technology, finance & sustainability.",
};

export const NAV_LINKS = [
  { id: "industries", label: "Industries" },
  { id: "services", label: "Services" },
  { id: "projects", label: "Case Studies" },
  { id: "approach", label: "Approach" },
  { id: "about", label: "About" },
  { id: "contact", label: "Contact" },
];

export const INDUSTRIES = [
  {
    key: "it",
    overline: "01 — Technology",
    title: "Information Technology",
    description:
      "Cloud transformation, platform engineering and delivery governance for software organisations scaling across borders.",
    image:
      "https://images.pexels.com/photos/7212946/pexels-photo-7212946.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    bullets: [
      "Multi-cloud architecture & FinOps",
      "Programme delivery for distributed engineering",
      "Compliance: ISO 27001, SOC 2, GDPR",
    ],
  },
  {
    key: "sustainability",
    overline: "02 — Sustainability",
    title: "Water & Climate Infrastructure",
    description:
      "Project leadership for municipal water systems, stormwater resilience and EU-funded sustainability programmes.",
    image:
      "https://images.unsplash.com/photo-1448099940878-e0c48ea3a165?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    bullets: [
      "EU Cohesion & Horizon-funded delivery",
      "Smart metering, SCADA & telemetry rollouts",
      "ESG reporting aligned to CSRD / GRI",
    ],
  },
  {
    key: "fintech",
    overline: "03 — Fintech",
    title: "Financial Technology",
    description:
      "From PSD2 onboarding to core-banking modernisation, we help regulated fintechs ship faster without breaking audit.",
    image:
      "https://images.pexels.com/photos/32396570/pexels-photo-32396570.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    bullets: [
      "Licensing support (KNF, BaFin, FCA)",
      "Payments, KYC/AML & open banking",
      "Core platform & ledger modernisation",
    ],
  },
];

export const SERVICES = [
  {
    code: "S01",
    title: "Business Management",
    body:
      "Operating model design, portfolio governance and executive reporting that gives boards a single source of truth.",
  },
  {
    code: "S02",
    title: "Project & Programme Management",
    body:
      "End-to-end delivery from discovery to handover — PMI, PRINCE2 and Agile practitioners embedded with your teams.",
  },
  {
    code: "S03",
    title: "Strategy & Advisory",
    body:
      "Market entry, M&A diligence, ESG strategy and digital transformation roadmaps for boards entering new geographies.",
  },
  {
    code: "S04",
    title: "Regulatory & Compliance",
    body:
      "GDPR, DORA, MiCA and CSRD readiness programmes — translated into engineering work that auditors actually accept.",
  },
  {
    code: "S05",
    title: "Technology Delivery",
    body:
      "Cloud, data platforms and platform engineering pods that ship measurable outcomes within the first 90 days.",
  },
  {
    code: "S06",
    title: "Capital & Funding Support",
    body:
      "Grant authoring and management for EU Cohesion, Horizon Europe and national innovation programmes.",
  },
];

export const CASE_STUDIES = [
  {
    id: "cs-01",
    industry: "Sustainability",
    location: "Kraków, Poland · Brno, Czechia",
    year: 2024,
    title: "Smart-metering rollout across two Central European water utilities",
    summary:
      "Programme leadership for a €42M cross-border deployment of NB-IoT smart meters covering 310,000 households, integrating SCADA telemetry with a unified billing platform.",
    metrics: [
      { label: "Households connected", value: "310k" },
      { label: "Non-revenue water reduction", value: "−18%" },
      { label: "Programme value", value: "€42M" },
    ],
    image:
      "https://images.unsplash.com/photo-1448099940878-e0c48ea3a165?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
  {
    id: "cs-02",
    industry: "Fintech",
    location: "Frankfurt, Germany",
    year: 2024,
    title: "Core-ledger modernisation for a Tier-2 European challenger bank",
    summary:
      "Re-platformed a monolithic ledger onto an event-sourced architecture across 11 markets, while remaining fully BaFin / EBA compliant throughout the cutover.",
    metrics: [
      { label: "Markets migrated", value: "11" },
      { label: "Settlement latency", value: "−63%" },
      { label: "Zero", value: "downtime cutover" },
    ],
    image:
      "https://images.pexels.com/photos/32396570/pexels-photo-32396570.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
  },
  {
    id: "cs-03",
    industry: "Information Technology",
    location: "Warsaw · Lisbon · Bucharest",
    year: 2023,
    title: "Programme office for a pan-European SaaS scale-up",
    summary:
      "Stood up a global PMO governing 14 engineering pods across three time zones — introducing OKRs, capacity planning and a unified delivery dashboard for the CEO and board.",
    metrics: [
      { label: "Engineering pods", value: "14" },
      { label: "On-time delivery", value: "92%" },
      { label: "Burn reduction", value: "−21%" },
    ],
    image:
      "https://images.unsplash.com/photo-1621831337128-35676ca30868?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
  {
    id: "cs-04",
    industry: "Sustainability",
    location: "Lake Garda Basin, Italy",
    year: 2023,
    title: "Drought-resilience masterplan for an alpine water basin",
    summary:
      "EU-funded feasibility and delivery roadmap for storage, reuse and irrigation optimisation across a 370 km² basin, co-authored with regional authorities.",
    metrics: [
      { label: "Basin coverage", value: "370 km²" },
      { label: "Water reuse target", value: "+24%" },
      { label: "Funding secured", value: "€11M" },
    ],
    image:
      "https://images.unsplash.com/photo-1505672678657-cc7037095e60?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
];

export const STATS = [
  { value: "€480M+", label: "Programme value under management" },
  { value: "17", label: "Countries delivered in" },
  { value: "120+", label: "Engagements since 2019" },
  { value: "94%", label: "Repeat / referral client rate" },
];

export const APPROACH = [
  {
    step: "01",
    title: "Diagnose",
    body: "Two-week structured assessment. Stakeholder interviews, data review and a written board-ready opinion — not a slide deck.",
  },
  {
    step: "02",
    title: "Design",
    body: "Operating model, delivery plan and commercial case. We size the team, the budget and the risk before any code or contract is signed.",
  },
  {
    step: "03",
    title: "Deliver",
    body: "Embedded programme leadership with weekly executive reporting. Outcome-based milestones, not time-and-materials creep.",
  },
  {
    step: "04",
    title: "Hand over",
    body: "We work ourselves out of a job. Documented playbooks, internal capability transfer and a 90-day aftercare retainer.",
  },
];

export const CLIENT_LOGOS = [
  "Vistula Capital",
  "Helion Water",
  "Northwind Bank",
  "Baltic Grid",
  "Astra Pay",
  "Meridian ESG",
  "Cyprian Holdings",
  "Verdant Utilities",
];

export const CERTIFICATIONS = [
  "ISO 27001",
  "ISO 9001",
  "PMI · PMP",
  "PRINCE2",
  "GDPR Compliant",
  "EU Vendor Registered",
];
